import { useMemo, useRef } from 'react';
import { useFrame } from '@react-three/fiber';
import * as THREE from 'three';
import type { WorldType } from '../types';
import { COLORS, MATERIALS, GAME } from '../constants';

// Floor style definitions
export type FloorStyle = 'stone' | 'grass' | 'wood' | 'marble' | 'dirt' | 'sand' | 'water' | 'ruins' | 'temple' | 'crystal' | 'sanctuary' | 'snow' | 'spatial' | 'wonderland';

interface FloorStyleConfig {
  baseColor: string;
  accentColor?: string;
  pattern: 'solid' | 'tiles' | 'organic';
  roughness: number;
  emissiveIntensity: number;
}

const FLOOR_STYLES: Record<FloorStyle, { real: FloorStyleConfig; spectral: FloorStyleConfig }> = {
  stone: {
    real: { baseColor: '#9a8b7a', pattern: 'solid', roughness: 0.85, emissiveIntensity: 0.05 },
    spectral: { baseColor: '#5a5a6e', pattern: 'solid', roughness: 0.85, emissiveIntensity: 0.1 },
  },
  grass: {
    real: { baseColor: '#6b8e4a', accentColor: '#5a7d3a', pattern: 'organic', roughness: 0.95, emissiveIntensity: 0.02 },
    spectral: { baseColor: '#3a5a4a', accentColor: '#2a4a3a', pattern: 'organic', roughness: 0.95, emissiveIntensity: 0.08 },
  },
  wood: {
    real: { baseColor: '#8b6b4a', accentColor: '#7a5a3a', pattern: 'tiles', roughness: 0.7, emissiveIntensity: 0.03 },
    spectral: { baseColor: '#5a4a3a', accentColor: '#4a3a2a', pattern: 'tiles', roughness: 0.7, emissiveIntensity: 0.1 },
  },
  marble: {
    real: { baseColor: '#e8e0d8', accentColor: '#d8d0c8', pattern: 'tiles', roughness: 0.3, emissiveIntensity: 0.08 },
    spectral: { baseColor: '#a8a0b8', accentColor: '#9890a8', pattern: 'tiles', roughness: 0.3, emissiveIntensity: 0.15 },
  },
  dirt: {
    real: { baseColor: '#8b7355', pattern: 'organic', roughness: 1, emissiveIntensity: 0.02 },
    spectral: { baseColor: '#5a4a3a', pattern: 'organic', roughness: 1, emissiveIntensity: 0.08 },
  },
  sand: {
    real: { baseColor: '#d4c4a8', accentColor: '#c8b898', pattern: 'organic', roughness: 0.95, emissiveIntensity: 0.05 },
    spectral: { baseColor: '#8a8a7a', accentColor: '#7a7a6a', pattern: 'organic', roughness: 0.95, emissiveIntensity: 0.1 },
  },
  water: {
    real: { baseColor: '#4a8ab0', accentColor: '#3a7aa0', pattern: 'organic', roughness: 0.1, emissiveIntensity: 0.1 },
    spectral: { baseColor: '#3a5a7a', accentColor: '#2a4a6a', pattern: 'organic', roughness: 0.1, emissiveIntensity: 0.2 },
  },
  ruins: {
    real: { baseColor: '#a09080', accentColor: '#908070', pattern: 'tiles', roughness: 0.9, emissiveIntensity: 0.03 },
    spectral: { baseColor: '#6a6a7a', accentColor: '#5a5a6a', pattern: 'tiles', roughness: 0.9, emissiveIntensity: 0.12 },
  },
  temple: {
    real: { baseColor: '#4a4a5a', accentColor: '#3a3a4a', pattern: 'tiles', roughness: 0.6, emissiveIntensity: 0.05 },
    spectral: { baseColor: '#2a2a4a', accentColor: '#1a1a3a', pattern: 'tiles', roughness: 0.6, emissiveIntensity: 0.15 },
  },
  crystal: {
    real: { baseColor: '#8ac0d0', accentColor: '#6ab0c0', pattern: 'tiles', roughness: 0.15, emissiveIntensity: 0.12 },
    spectral: { baseColor: '#5a80a0', accentColor: '#4a70b0', pattern: 'tiles', roughness: 0.15, emissiveIntensity: 0.25 },
  },
  sanctuary: {
    real: { baseColor: '#f0e8d8', accentColor: '#e8d8c0', pattern: 'tiles', roughness: 0.25, emissiveIntensity: 0.1 },
    spectral: { baseColor: '#c0b8d8', accentColor: '#b0a8c8', pattern: 'tiles', roughness: 0.25, emissiveIntensity: 0.2 },
  },
  snow: {
    real: { baseColor: '#f8f8ff', accentColor: '#e8e8f0', pattern: 'organic', roughness: 0.9, emissiveIntensity: 0.15 },
    spectral: { baseColor: '#2a1a3a', accentColor: '#3a2a4a', pattern: 'organic', roughness: 0.85, emissiveIntensity: 0.08 },
  },
  spatial: {
    // Sol spatial noir avec étoiles
    real: { baseColor: '#050510', accentColor: '#ffffff', pattern: 'solid', roughness: 0.1, emissiveIntensity: 0.02 },
    spectral: { baseColor: '#0a0520', accentColor: '#9370db', pattern: 'solid', roughness: 0.1, emissiveIntensity: 0.05 },
  },
  wonderland: {
    // Monde réel: Jardin de thé avec herbe verte
    real: { baseColor: '#4a7a3a', accentColor: '#3a6a2a', pattern: 'organic', roughness: 0.95, emissiveIntensity: 0.03 },
    // Monde spectral: Herbe verte sombre (jardin de la Reine)
    spectral: { baseColor: '#2a4a2a', accentColor: '#1a3a1a', pattern: 'organic', roughness: 0.95, emissiveIntensity: 0.05 },
  },
};

interface IsometricFloorProps {
  width?: number;
  depth?: number;
  world: WorldType;
  showGrid?: boolean;
  floorStyle?: FloorStyle;
}

export function IsometricFloor({
  width = 10,
  depth = 10,
  world,
  showGrid = false,
  floorStyle = 'stone',
}: IsometricFloorProps) {
  const colors = world === 'real' ? COLORS.real : COLORS.spectral;
  const tileSize = GAME.tileSize;
  const styleConfig = FLOOR_STYLES[floorStyle][world];

  // Create noise-like variation for organic patterns
  const getColorVariation = useMemo(() => {
    if (styleConfig.pattern !== 'organic') return null;

    // Simple pseudo-random function based on position
    return (x: number, z: number) => {
      const noise = Math.sin(x * 12.9898 + z * 78.233) * 43758.5453;
      return (noise - Math.floor(noise)) * 0.15 - 0.075; // -0.075 to 0.075 variation
    };
  }, [styleConfig.pattern]);

  return (
    <group position={[(width * tileSize) / 2, 0, (depth * tileSize) / 2]}>
      {/* Main floor */}
      <mesh receiveShadow rotation={[-Math.PI / 2, 0, 0]}>
        <planeGeometry args={[width * tileSize, depth * tileSize]} />
        <meshStandardMaterial
          color={styleConfig.baseColor}
          roughness={styleConfig.roughness}
          metalness={MATERIALS.flat.metalness}
          flatShading={false}
          emissive={styleConfig.baseColor}
          emissiveIntensity={styleConfig.emissiveIntensity}
        />
      </mesh>

      {/* Tile pattern for non-solid floors */}
      {styleConfig.pattern === 'tiles' && styleConfig.accentColor && (
        <>
          {Array.from({ length: Math.floor(width / 2) }).map((_, xi) =>
            Array.from({ length: Math.floor(depth / 2) }).map((_, zi) => {
              const x = (xi * 2 + 1) * tileSize - (width * tileSize) / 2;
              const z = (zi * 2 + 1) * tileSize - (depth * tileSize) / 2;
              return (
                <mesh
                  key={`tile-${xi}-${zi}`}
                  position={[x, 0.002, z]}
                  rotation={[-Math.PI / 2, 0, 0]}
                  receiveShadow
                >
                  <planeGeometry args={[tileSize * 0.95, tileSize * 0.95]} />
                  <meshStandardMaterial
                    color={styleConfig.accentColor}
                    roughness={styleConfig.roughness}
                    emissive={styleConfig.accentColor}
                    emissiveIntensity={styleConfig.emissiveIntensity * 0.8}
                  />
                </mesh>
              );
            })
          ).flat()}
        </>
      )}

      {/* Organic pattern decorations (grass tufts, dirt patches, etc.) */}
      {styleConfig.pattern === 'organic' && styleConfig.accentColor && (
        <>
          {Array.from({ length: Math.floor(width * depth / 3) }).map((_, i) => {
            const seed = i * 0.1;
            const x = (Math.sin(seed * 123.456) * 0.5 + 0.5) * width * tileSize - (width * tileSize) / 2;
            const z = (Math.cos(seed * 789.012) * 0.5 + 0.5) * depth * tileSize - (depth * tileSize) / 2;
            const size = 0.3 + Math.sin(seed * 345.678) * 0.2;
            return (
              <mesh
                key={`organic-${i}`}
                position={[x, 0.001, z]}
                rotation={[-Math.PI / 2, seed * 10, 0]}
                receiveShadow
              >
                <circleGeometry args={[size, 6]} />
                <meshStandardMaterial
                  color={styleConfig.accentColor}
                  roughness={styleConfig.roughness}
                  emissive={styleConfig.accentColor}
                  emissiveIntensity={styleConfig.emissiveIntensity * 0.6}
                  transparent
                  opacity={0.6}
                />
              </mesh>
            );
          })}
        </>
      )}

      {/* Spatial floor stars */}
      {floorStyle === 'spatial' && (
        <SpatialStars width={width} depth={depth} world={world} tileSize={tileSize} />
      )}

      {/* Grid overlay for editor only */}
      {showGrid && (
        <gridHelper
          args={[
            Math.max(width, depth) * tileSize,
            Math.max(width, depth),
            '#ffffff',
            '#ffffff',
          ]}
          position={[0, 0.01, 0]}
        >
          <meshBasicMaterial transparent opacity={0.1} />
        </gridHelper>
      )}
    </group>
  );
}

// Component for spatial floor stars
function SpatialStars({ width, depth, world, tileSize }: {
  width: number;
  depth: number;
  world: WorldType;
  tileSize: number;
}) {
  const starsRef = useRef<THREE.Group>(null);

  // Generate star positions
  const stars = useMemo(() => {
    const starCount = Math.floor(width * depth * 3); // ~3 stars per tile
    const starArray: { x: number; z: number; size: number; brightness: number; twinkleSpeed: number }[] = [];

    for (let i = 0; i < starCount; i++) {
      const seed = i * 0.7654321;
      const x = (Math.sin(seed * 123.456) * 0.5 + 0.5) * width * tileSize - (width * tileSize) / 2;
      const z = (Math.cos(seed * 789.012) * 0.5 + 0.5) * depth * tileSize - (depth * tileSize) / 2;
      const size = 0.02 + Math.sin(seed * 345.678) * 0.015;
      const brightness = 0.5 + Math.sin(seed * 567.89) * 0.5;
      const twinkleSpeed = 0.5 + Math.sin(seed * 901.234) * 1.5;

      starArray.push({ x, z, size, brightness, twinkleSpeed });
    }
    return starArray;
  }, [width, depth, tileSize]);

  // Larger stars (nebula points)
  const nebulaStars = useMemo(() => {
    const count = Math.floor(width * depth / 4);
    const nebulaArray: { x: number; z: number; size: number; color: string }[] = [];

    for (let i = 0; i < count; i++) {
      const seed = i * 1.23456;
      const x = (Math.sin(seed * 234.567) * 0.5 + 0.5) * width * tileSize - (width * tileSize) / 2;
      const z = (Math.cos(seed * 890.123) * 0.5 + 0.5) * depth * tileSize - (depth * tileSize) / 2;
      const size = 0.08 + Math.sin(seed * 456.789) * 0.06;
      // Colors: purple, blue, pink, gold
      const colors = world === 'spectral'
        ? ['#9370db', '#6a5acd', '#ff69b4', '#4169e1']
        : ['#ffd700', '#87ceeb', '#ffffff', '#add8e6'];
      const color = colors[Math.floor(Math.sin(seed * 678.901) * colors.length + colors.length) % colors.length];

      nebulaArray.push({ x, z, size, color });
    }
    return nebulaArray;
  }, [width, depth, tileSize, world]);

  // Animate twinkling
  useFrame((state) => {
    if (!starsRef.current) return;
    const time = state.clock.elapsedTime;

    starsRef.current.children.forEach((child, i) => {
      if (i < stars.length && child instanceof THREE.Mesh) {
        const star = stars[i];
        const twinkle = 0.5 + Math.sin(time * star.twinkleSpeed + i) * 0.5;
        const material = child.material as THREE.MeshBasicMaterial;
        if (material && 'opacity' in material) {
          material.opacity = star.brightness * twinkle;
        }
      }
    });
  });

  const starColor = world === 'spectral' ? '#b0a0d0' : '#ffffff';

  return (
    <group ref={starsRef}>
      {/* Small twinkling stars */}
      {stars.map((star, i) => (
        <mesh
          key={`star-${i}`}
          position={[star.x, 0.005, star.z]}
          rotation={[-Math.PI / 2, 0, 0]}
        >
          <circleGeometry args={[star.size, 4]} />
          <meshBasicMaterial
            color={starColor}
            transparent
            opacity={star.brightness}
          />
        </mesh>
      ))}

      {/* Larger nebula-like points */}
      {nebulaStars.map((star, i) => (
        <mesh
          key={`nebula-${i}`}
          position={[star.x, 0.003, star.z]}
          rotation={[-Math.PI / 2, 0, 0]}
        >
          <circleGeometry args={[star.size, 8]} />
          <meshBasicMaterial
            color={star.color}
            transparent
            opacity={0.4}
          />
        </mesh>
      ))}

      {/* Subtle glow spots */}
      {nebulaStars.filter((_, i) => i % 3 === 0).map((star, i) => (
        <mesh
          key={`glow-${i}`}
          position={[star.x, 0.002, star.z]}
          rotation={[-Math.PI / 2, 0, 0]}
        >
          <circleGeometry args={[star.size * 3, 16]} />
          <meshBasicMaterial
            color={star.color}
            transparent
            opacity={0.1}
          />
        </mesh>
      ))}
    </group>
  );
}
